import React from "react";
import { useSelector } from "react-redux";
import BuildingIcon from "@react-spectrum/s2/icons/Building";
import UserIcon from "@react-spectrum/s2/icons/User";
import UserGroupIcon from "@react-spectrum/s2/icons/UserGroup";
import EnterpriseIcon from "@react-spectrum/s2/icons/Enterprise";
import WalletIcon from "@react-spectrum/s2/icons/Wallet";
import CheckmarkIcon from "@react-spectrum/s2/icons/Checkmark";
import { View, Flex, Text, asS2Icon } from "./Common/spectrumCompat";
import { getSupportCaseLink } from "../Helpers/utils";
import { getWizardNavSteps } from "./registrationNavSteps";
import "./css/registration.css";

const Building = asS2Icon(BuildingIcon);
const User = asS2Icon(UserIcon);
const UserGroup = asS2Icon(UserGroupIcon);
const Enterprise = asS2Icon(EnterpriseIcon);
const Wallet = asS2Icon(WalletIcon);
const Checkmark = asS2Icon(CheckmarkIcon);

export type LeftNavigationPreviewProps = {
  /** When set, use this step for highlighting instead of Redux (e.g. stacked UI preview). */
  previewStepOverride?: number;
  /** When set, build step list as this company type instead of Redux (preview only). */
  previewCompanyTypeOverride?: "standaloneCompany" | "subsidaryCompany" | null;
};

type AppState = {
  registration?: {
    partnerRegistrationFormStep?: number;
    selectedRegisterCompanyType?: string | null;
  };
};

function LeftNavigation({
  previewStepOverride,
  previewCompanyTypeOverride,
}: LeftNavigationPreviewProps) {
  const registrationState = useSelector(
    (state: AppState) => state.registration,
  );
  const currentStep =
    previewStepOverride !== undefined
      ? previewStepOverride
      : registrationState?.partnerRegistrationFormStep || 0;
  /** Match wizard pages (e.g. CompanyInformation): Redux may be empty after refresh while localStorage still holds the flow. */
  const selectedRegisterCompanyType =
    previewCompanyTypeOverride !== undefined
      ? previewCompanyTypeOverride
      : (registrationState?.selectedRegisterCompanyType ??
        (typeof localStorage !== "undefined"
          ? localStorage.getItem("selectedRegisterCompanyType")
          : null));

  if (currentStep === 0) {
    return null;
  }

  const iconComponents = [Building, User, UserGroup, Enterprise, Wallet];
  const stepMetas = getWizardNavSteps(selectedRegisterCompanyType);
  const navigationSteps = stepMetas.map((meta, index) => {
    const IconComp = iconComponents[index];
    return {
      key: meta.key,
      label: meta.label,
      icon: IconComp ? <IconComp /> : <Building />,
    };
  });

  const stepRowGap = "clamp(0.5rem, 1rem + 0.3vw, 1.25rem)";

  return (
    <View UNSAFE_className="left-navigation-container dxp-wizard-sidebar-nav dxp-nav-root">
      <Flex direction="column" UNSAFE_className="dxp-nav-inner">
        <Text UNSAFE_className="dxp-nav-program-label">
          ADOBE DIGITAL EXPERIENCE PARTNER PROGRAM
        </Text>
        <Text UNSAFE_className="dxp-nav-heading">Create account</Text>

        <Flex
          direction="column"
          UNSAFE_className="dxp-wizard-sidebar-steps dxp-nav-steps-track"
          height="fit-content"
        >
          {navigationSteps.map((step, index) => (
            <View key={step.key}>
              <View UNSAFE_className="dxp-nav-step-row-hit">
                <Flex
                  direction="row"
                  alignItems="center"
                  gap={stepRowGap}
                  width="100%"
                >
                  <Flex
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    UNSAFE_className="dxp-nav-icon-track"
                  >
                    <View UNSAFE_className="dxp-nav-icon-circle">
                      <View UNSAFE_className="dxp-nav-icon-fg">
                        {step.icon}
                      </View>

                      {step.key < currentStep ? (
                        <Checkmark UNSAFE_className="dxp-nav-step-checkmark" />
                      ) : null}
                    </View>
                  </Flex>

                  <View
                    UNSAFE_className={
                      step.key === currentStep
                        ? "dxp-nav-step-label-cell dxp-nav-step-label-cell--current"
                        : "dxp-nav-step-label-cell"
                    }
                  >
                    <Text UNSAFE_className="dxp-nav-step-label-text">
                      {step.label}
                    </Text>
                  </View>
                </Flex>
              </View>
              {index < navigationSteps.length - 1 ? (
                <Flex
                  direction="row"
                  alignItems="stretch"
                  gap={stepRowGap}
                  width="100%"
                >
                  <View UNSAFE_className="dxp-nav-connector-band">
                    <View UNSAFE_className="dxp-nav-connector-line" />
                  </View>
                  <View UNSAFE_className="dxp-nav-connector-spacer" />
                </Flex>
              ) : null}
            </View>
          ))}
        </Flex>

        <View UNSAFE_className="dxp-nav-support-wrap">
          <Text UNSAFE_className="dxp-nav-support-text">
            Questions?{" "}
            <Text>
              <button
                type="button"
                className="dxp-nav-support-link"
                onClick={() => {
                  window.open(getSupportCaseLink(), "_blank");
                }}
              >
                Contact our support team
              </button>
            </Text>
          </Text>
        </View>
      </Flex>
    </View>
  );
}

LeftNavigation.defaultProps = {
  previewStepOverride: undefined,
  previewCompanyTypeOverride: undefined,
};

export default LeftNavigation;
